import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (!sessionId || hasPolled.current) return;
    hasPolled.current = true;

    const token = localStorage.getItem('pulse_token');

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 6) {
        setStatus('error');
        return;
      }

      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }
        setTimeout(() => pollStatus(attempts + 1), 2500);
      } catch {
        if (attempts < 3) {
          setTimeout(() => pollStatus(attempts + 1), 2500);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="max-w-lg w-full shadow-lg" data-testid="payment-success-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img src="/static/images/pulse-email-logo.png" alt="Pulse" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {status === 'checking' && 'Processing Payment...'}
            {status === 'paid' && 'Thank You for Your Payment!'}
            {status === 'error' && 'Payment Status'}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {status === 'checking' && 'Please wait while we confirm your payment with Stripe.'}
            {status === 'paid' && 'Your subscription has been activated successfully. You\'re all set!'}
            {status === 'error' && 'We could not confirm your payment yet. It may take a moment to process. Please check your email for confirmation from Stripe.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-6 pb-8">
          {status === 'checking' && (
            <Loader2 className="h-16 w-16 text-[#5AA2AE] animate-spin" data-testid="payment-loading" />
          )}
          {status === 'paid' && (
            <>
              <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center" data-testid="payment-success-icon">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              {paymentData && paymentData.amount_total > 0 && (
                <div className="text-center text-sm text-slate-500 space-y-1">
                  <p>Amount: <span className="font-semibold text-slate-700">${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}/mo</span></p>
                </div>
              )}
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Link to="/manage-account">
                  <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90" data-testid="go-to-account-btn">
                    Go to Manage Account
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button variant="outline" className="w-full" data-testid="go-to-projects-btn">
                    Go to Projects
                  </Button>
                </Link>
              </div>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="h-20 w-20 rounded-full bg-amber-50 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-amber-500" />
              </div>
              <p className="text-sm text-slate-500 text-center max-w-sm">
                If you completed the payment, your subscription will be activated shortly. You can also check your invoice history in Manage Account.
              </p>
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Link to="/manage-account">
                  <Button className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90" data-testid="go-to-account-btn">
                    Go to Manage Account
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="w-full" data-testid="back-to-pricing-btn">Back to Pricing</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
